import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api.js';

export default function InitialPlanConfirmation() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planId = searchParams.get('plan') ?? '';
  const payment = searchParams.get('payment') ?? '';
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api
      .listPublicPlans()
      .then(setPlans)
      .catch((err) => setError(err.message));
  }, []);

  const selectedPlan = useMemo(() => plans.find((plan) => plan.id === planId) ?? null, [plans, planId]);
  const isFreePlan = selectedPlan?.priceCents === 0;
  const paymentState = payment === 'ok' ? 'success' : payment === 'cancel' ? 'cancel' : 'idle';
  const rail = selectedPlan?.slug === 'free' ? 'bg-[#00CA72]' : selectedPlan?.slug === 'pro' ? 'bg-[#6161FF]' : 'bg-slate-900';

  async function continueFlow() {
    if (!selectedPlan) {
      navigate('/dashboard');
      return;
    }

    if (paymentState === 'success') {
      navigate('/dashboard');
      return;
    }

    setBusy(true);
    setError('');
    try {
      const baseUrl = window.location.origin;
      const result = await api.initialPlanCheckout({
        planId: selectedPlan.id,
        successUrl: `${baseUrl}/#/assinatura-inicial?plan=${selectedPlan.id}&payment=ok`,
        cancelUrl: `${baseUrl}/#/assinatura-inicial?plan=${selectedPlan.id}&payment=cancel`,
      });
      if (result.url) {
        window.location.href = result.url;
        return;
      }
      navigate('/dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="relative min-h-screen bg-[#eef2f7]">
      <div
        aria-hidden="true"
        className="absolute inset-y-0 right-0 w-[55%] bg-[linear-gradient(180deg,#dcecff_0%,#c7e4ff_46%,#9ee4f0_100%)]"
      />
      <div className="relative mx-auto max-w-6xl px-6 py-10 lg:px-8">
        <div className="mb-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-500 via-cyan-400 to-teal-300 text-white shadow-[0_16px_32px_-18px_rgba(14,165,233,0.9)]">
              <span className="material-symbols-outlined text-[18px]">sell</span>
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-sky-700">EtiquetandoApp</p>
              <p className="text-[11px] text-slate-500">Estúdio de geração e impressão</p>
            </div>
          </div>
          <span className="text-[11px] font-semibold text-slate-400">Confirmar plano</span>
        </div>

        <div className="mx-auto grid max-w-5xl gap-6 lg:grid-cols-2">
          <section className="rounded-2xl bg-[#181B34] p-7">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-[10px] font-bold text-white">
              <span className="material-symbols-outlined text-[13px]">check_circle</span>
              Conta criada
            </div>

            <h1 className="studio-uniform-title mt-4 font-h2 text-h2 font-extrabold leading-tight tracking-[-0.02em] text-white">
              Seu espaço está pronto para começar.
            </h1>
            <p className="mt-3 text-[11px] leading-6 text-[#C9CCDE]">
              Você concluiu o cadastro do EtiquetandoApp. Agora falta confirmar a etapa inicial do plano para entrar com
              a operação de etiquetas já alinhada.
            </p>

            <div className="mt-7 divide-y divide-white/10 rounded-xl border border-white/10 bg-white/5 text-[11px]">
              <StatusRow
                icon="sell"
                label="Plano selecionado"
                value={selectedPlan?.name ?? 'Plano não localizado'}
                helper={
                  selectedPlan
                    ? isFreePlan
                      ? 'Ativação sem cobrança'
                      : 'Próximo passo: contratação inicial'
                    : 'Escolha um plano para seguir'
                }
              />
              <StatusRow
                icon="workspace_premium"
                label="Estado da conta"
                value={paymentState === 'success' ? 'Pronta para entrar' : 'Conta criada'}
                helper={
                  paymentState === 'cancel'
                    ? 'Sua conta permanece em trial enquanto a contratação não conclui.'
                    : 'Tenant e usuário já foram criados com sucesso.'
                }
              />
              <StatusRow
                icon="print"
                label="operação"
                value="Fluxo de etiquetas preparado"
                helper="Modelos, dados variáveis e impressão podem seguir após esta etapa."
              />
            </div>
          </section>

          <section className="relative flex flex-col rounded-2xl border border-slate-200 bg-white">
            <div className={`absolute inset-x-0 top-0 h-1.5 rounded-t-2xl ${rail}`} />
            <div className="flex flex-1 flex-col justify-between p-7">
              <div>
                <p className="text-[11px] font-bold text-slate-500">Confirmação do onboarding</p>
                <h2 className="studio-uniform-title mt-2 font-h2 text-h2 font-extrabold tracking-[-0.02em] text-[#181B34]">
                  {paymentState === 'success'
                    ? 'Contratação concluída com sucesso.'
                    : paymentState === 'cancel'
                      ? 'A contratação foi interrompida.'
                      : 'Revise seu plano e siga para a próxima etapa.'}
                </h2>
                <p className="mt-3 text-[11px] leading-6 text-slate-600">
                  {paymentState === 'success'
                    ? 'Seu retorno do checkout foi recebido. Você já pode entrar no painel e continuar a configuração da operação.'
                    : paymentState === 'cancel'
                      ? 'Nenhum dado foi perdido. Sua conta continua criada e a assinatura permanece em trial até você retomar a contratação.'
                      : 'A jornada de entrada passa por esta confirmação antes do dashboard, mantendo o plano e a contratação conectados desde o início.'}
                </p>

                {selectedPlan ? (
                  <div className="mt-6 rounded-xl border border-slate-200">
                    <div className="flex items-start justify-between gap-4 px-5 pt-5">
                      <div>
                        <p className="text-[10px] font-bold text-slate-400">Resumo do plano</p>
                        <h3 className="studio-uniform-title mt-2 font-h3 text-h3 font-extrabold text-[#181B34]">{selectedPlan.name}</h3>
                        <p className="mt-1.5 text-[11px] leading-6 text-slate-600">{selectedPlan.description}</p>
                      </div>
                      <span className="material-symbols-outlined rounded-lg bg-[#F0F3FF] p-2.5 text-[16px] text-[#4F46E5]">
                        inventory_2
                      </span>
                    </div>

                    <div className="flex items-baseline gap-2 px-5 pb-5">
                      <span className="text-[1.75rem] font-extrabold tracking-[-0.03em] text-[#181B34]">
                        {selectedPlan.priceCents === 0 ? 'Grátis' : formatMoney(selectedPlan.priceCents, selectedPlan.currency)}
                      </span>
                      <span className="text-[11px] text-slate-500">
                        {selectedPlan.priceCents === 0
                          ? 'sem cobrança'
                          : `por ${selectedPlan.period === 'YEARLY' ? 'ano' : 'mês'}`}
                      </span>
                    </div>

                    <div className="divide-y divide-slate-100 border-t border-slate-100 text-[11px]">
                      <SpecRow label="Usuários" value={selectedPlan.maxUsers >= 100 ? 'Escala alta' : `${selectedPlan.maxUsers}`} />
                      <SpecRow label="Modelos" value={selectedPlan.maxTemplates >= 1000 ? 'Escala alta' : `${selectedPlan.maxTemplates}`} />
                    </div>

                    {(selectedPlan.features ?? []).length > 0 && (
                      <ul className="space-y-2 px-5 py-5">
                        {selectedPlan.features.map((feature) => (
                          <li key={feature} className="flex items-start gap-2 text-[11px] leading-6 text-slate-700">
                            <span className="material-symbols-outlined mt-0.5 text-[14px] text-[#4F46E5]">check_circle</span>
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ) : (
                  <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-[11px] text-amber-800">
                    Não conseguimos localizar o plano atual. Você ainda pode entrar no sistema ou voltar para a seleção de
                    planos.
                  </div>
                )}

                {paymentState === 'cancel' && (
                  <div className="mt-6 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-[11px] text-sky-800">
                    Sua conta segue criada em modo de avaliação. Você pode retomar a contratação agora ou mais tarde, sem
                    refazer o cadastro.
                  </div>
                )}

                {error && (
                  <div className="mt-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[11px] text-red-700">{error}</div>
                )}
              </div>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={continueFlow}
                  disabled={busy}
                  className="inline-flex min-h-11 flex-1 items-center justify-center rounded-xl bg-[#6161FF] px-5 text-[11px] font-bold text-white transition-colors hover:bg-[#4F4FEA] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {busy
                    ? 'Processando...'
                    : paymentState === 'success'
                      ? 'Entrar no painel'
                      : isFreePlan
                        ? 'Concluir ativação'
                        : 'Ir para contratação'}
                </button>
                <Link
                  to="/dashboard"
                  className="inline-flex min-h-11 items-center justify-center rounded-xl border border-[#CFD2E5] bg-white px-5 text-[11px] font-semibold text-[#181B34] transition-colors hover:bg-[#F0F3FF]"
                >
                  Ir para o painel
                </Link>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

function StatusRow({ icon, label, value, helper }) {
  return (
    <div className="flex items-start gap-3 px-4 py-4">
      <span className="material-symbols-outlined rounded-lg bg-white/10 p-2 text-[14px] text-white">{icon}</span>
      <div className="min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-[0.06em] text-[#9A9DB8]">{label}</p>
        <p className="mt-0.5 text-[11px] font-bold text-white">{value}</p>
        <p className="mt-1 text-[10px] leading-5 text-[#9A9DB8]">{helper}</p>
      </div>
    </div>
  );
}

function SpecRow({ label, value }) {
  return (
    <div className="flex items-center justify-between px-5 py-3">
      <span className="text-slate-500">{label}</span>
      <span className="font-bold text-[#181B34]">{value}</span>
    </div>
  );
}

function formatMoney(cents, currency = 'BRL') {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency,
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);
}


